import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import { availableGroups, isValidGroup } from "../../api/Login";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { PageNotFound } from "../common/PageNotFound/PageNotFound";
import { renderError } from "../../common/helperfunctions/ApiError";
interface GroupRoutingParams {
  group: string;
}
interface Opts {
  group: string;
}
interface GroupProviderProps {
  children: ({ group }: { group: string }) => React.ReactNode;
}
/**
 * GroupProvider component is responsible for fetching and providing group data to its children.
 * It uses the `useParams` hook to get the current group from the URL parameters and the `useQuery` hook
 * to fetch the available groups.
 * @author @CorradoSurmanowicz
 * @param {GroupProviderProps} props - The properties for the GroupProvider component.
 * @param {React.ReactNode} props.children - The child components that will receive the group data.
 *
 * @returns {JSX.Element} The rendered component, which includes a LoadingWrapper that handles the loading state,
 * and either the children components with the group data or a PageNotFound component if the group is not found.
 */
export const GroupProvider = ({ children }: GroupProviderProps) => {
  const { group } = useParams<GroupRoutingParams>();
  const [isValid, setIsValid] = React.useState<boolean | undefined>(undefined);
  const { mutateAsync: validateGroup } = useMutation<boolean, unknown, Opts>(
    (options: Opts) => {
      return isValidGroup(options.group);
    },
    {
      onError: (err, variables) => {
        renderError(err);
      },
    }
  );
  const groupsQuery = useQuery({
    queryKey: ["groups"],
    queryFn: async ({ signal }) => await availableGroups(signal),
    // cacheTime: Infinity,
    // staleTime: Infinity,
    onSuccess: (data) => {
      if (Array.isArray(data) && !!data.length && data.find((g) => g.name === group)) {
        setIsValid(true);
      } else if (Array.isArray(data) && !data.length) {
        // Case 2: API returns empty array (e.g. disable_public_groups feature flag)
        validateGroup({ group: group }).then((isValid) => {
          setIsValid(isValid);
        });
      } else {
        setIsValid(false);
      }
    },
  });
  const { status: groupsStatus, fetchStatus: groupsFetchStatus, error: groupError } = groupsQuery;

  return (
    <LoadingWrapper status={groupsStatus} fetchStatus={groupsFetchStatus} error={groupError}>
      {!isValid ? <PageNotFound /> : children({ group })}
    </LoadingWrapper>
  );
};
